import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { LoginPage } from './auth/LoginPage';
import { Shell } from './components/Shell';
import { ToastProvider } from './components/ui';

import { AdsPage } from './pages/Ads';
import { AgentsPage } from './pages/Agents';
import { AnalyticsPage } from './pages/Analytics';
import { AuditPage } from './pages/Audit';
import { LoginHistoryPage } from './pages/LoginHistory';
import { SecurityPage } from './pages/Security';
import { BlockedPage } from './pages/Blocked';
import { BonusesPage } from './pages/Bonuses';
import { GapIdsPage } from './pages/GapIds';
import { BookingsPage } from './pages/Bookings';
import { CategoriesPage } from './pages/Categories';
import { CategoryModerationPage } from './pages/CategoryModeration';
import { ChannelsPage } from './pages/Channels';
import { ChatModerationPage } from './pages/ChatModeration';
import { CrmPage } from './pages/Crm';
import { JobsPage } from './pages/Jobs';
import { TeamPage } from './pages/Team';
import { DashboardPage } from './pages/Dashboard';
import { FiltersPage } from './pages/Filters';
import { GstPage } from './pages/Gst';
import { KycPage } from './pages/Kyc';
import { ListingsPage } from './pages/Listings';
import { LocationsPage } from './pages/Locations';
import { PaymentsPage } from './pages/Payments';
import { CommunitiesPage } from './pages/Communities';
import { FreeSlotsPage } from './pages/FreeSlots';
import { FreePostsPage } from './pages/FreePosts';
import { PropertiesPage } from './pages/Properties';
import { KycManagementPage } from './pages/KycManagement';
import { WatermarkPage } from './pages/Watermark';
import { ContactVisibilityPage } from './pages/ContactVisibility';
import { ProviderMediaPage } from './pages/ProviderMedia';
import { PlansPage } from './pages/Plans';
import { ProfitDistributionPage } from './pages/ProfitDistribution';
import { PoliceVerificationPage } from './pages/PoliceVerification';
import { ProvidersPage } from './pages/Providers';
import { ReportsPage } from './pages/Reports';
import { SettingsPage } from './pages/Settings';
import { UserDetailPage } from './pages/UserDetail';
import { UsersPage } from './pages/Users';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // An operator working a queue expects fresh counts; 30s is short enough
      // to feel live and long enough that switching screens is instant.
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      // A 401 is handled by the client's refresh, and a 403/404 will not fix
      // itself — only retry what might be transient.
      retry: (count, error) => {
        const status = (error as { status?: number }).status ?? 0;
        return status >= 500 && count < 2;
      },
    },
  },
});

function Gate() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', background: 'var(--bg)' }}>
        <div className="spinner spinner--dark" style={{ width: 26, height: 26 }} />
      </div>
    );
  }

  if (!user) return <LoginPage />;

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<DashboardPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />

        <Route path="kyc" element={<KycPage />} />
        <Route path="police-verifications" element={<PoliceVerificationPage />} />
        <Route path="moderation/categories" element={<CategoryModerationPage />} />
        <Route path="reports" element={<ReportsPage />} />

        <Route path="users" element={<UsersPage />} />
        <Route path="users/:id" element={<UserDetailPage />} />
        <Route path="providers" element={<ProvidersPage />} />
        <Route path="agents" element={<AgentsPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="crm" element={<CrmPage />} />

        <Route path="listings" element={<ListingsPage />} />
        <Route path="jobs" element={<JobsPage />} />
        <Route path="bookings" element={<BookingsPage />} />
        <Route path="chat" element={<ChatModerationPage />} />
        <Route path="ads" element={<AdsPage />} />

        <Route path="payments" element={<PaymentsPage />} />
        <Route path="gst" element={<GstPage />} />
        <Route path="plans" element={<PlansPage />} />
        <Route path="communities" element={<CommunitiesPage />} />
        <Route path="free-slots" element={<FreeSlotsPage />} />
        <Route path="free-posts" element={<FreePostsPage />} />
        <Route path="properties" element={<PropertiesPage />} />
        <Route path="kyc-management" element={<KycManagementPage />} />
        <Route path="watermark" element={<WatermarkPage />} />
        <Route path="contact-visibility" element={<ContactVisibilityPage />} />
        <Route path="provider-media" element={<ProviderMediaPage />} />
        <Route path="bonuses" element={<BonusesPage />} />
        <Route path="gap-ids" element={<GapIdsPage />} />
        <Route path="membership" element={<Navigate to="/communities" replace />} />
        <Route path="profit-sharing" element={<BlockedPage module="profit-sharing" />} />

        <Route path="categories" element={<CategoriesPage />} />
        <Route path="filters" element={<FiltersPage />} />
        <Route path="locations" element={<LocationsPage />} />
        <Route path="channels" element={<ChannelsPage />} />

        <Route path="notifications" element={<NotificationsLazy />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/profit" element={<ProfitDistributionPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="login-history" element={<LoginHistoryPage />} />
        <Route path="security" element={<SecurityPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

// Imported separately so the composer's preview markup does not sit in the
// initial bundle for an operator who only ever works the KYC queue.
import { NotificationsPage } from './pages/Notifications';
function NotificationsLazy() {
  return <NotificationsPage />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <AuthProvider>
            <Gate />
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
